/**
 * ============================================================================
 *  File        : frontend/src/api/weights.ts
 *  Module      : api
 *  Purpose     : 20kg 重量差異 review 型別化 API（S3）— list / create / resolve
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端 handler：crates/api-http/src/routes/v13_weight_variance_reviews.rs，掛在 v1：
 *    GET  /api/v1/cases/:case_id/weight-audits          -> list_reviews（marketplace.weight_variance.view）
 *    POST /api/v1/cases/:case_id/weight-audits          -> create_review（marketplace.weight_variance.create）
 *    POST /api/v1/weight-audits/:review_id/resolution   -> submit_resolution（marketplace.weight_variance.resolve）
 *  Decimal 欄位（重量 / 差異）request 以「字串」送（後端以 Decimal 解析），response 為字串。
 *  20kg 規則為後端權威：response 的 threshold_kg / variance_kg / status 為唯一判定來源；
 *  前端僅以 abs(member - venue) > 20 做輸入階段的提示 highlight，不自行裁定權威重量。
 */

import { API_BASE, mutationHeaders, requestData, requestEnvelope, type Envelope } from './client'
import type { ApiSchemas } from './client'

/** 重量差異 review DTO（= 後端 WeightVarianceReviewDto）。 */
export type WeightVarianceReviewDto = ApiSchemas['WeightVarianceReviewDto']
/** create review 後的評估結果（= 後端 WeightVarianceEvalDto）。 */
export type WeightVarianceEvalDto = ApiSchemas['WeightVarianceEvalDto']
/** 建立 review 的 request body（= 後端 CreateWeightVarianceReviewDto）。 */
export type CreateWeightVarianceReviewDto = ApiSchemas['CreateWeightVarianceReviewDto']
/** 雙方提出解決方案的 request body（= 後端 ResolutionDto）。 */
export type ResolutionDto = ApiSchemas['ResolutionDto']

/** spec §D 20kg 閾值（前端輸入階段提示用；權威判定仍以後端 response 為準）。 */
export const WEIGHT_VARIANCE_THRESHOLD_KG = 20

const CASES_PATH = `${API_BASE}/cases`
const REVIEWS_PATH = `${API_BASE}/weight-audits`

/** 列出案件的所有重量差異 review。 */
export function listWeightVarianceReviews(
  caseId: number | string,
): Promise<Envelope<WeightVarianceReviewDto[]>> {
  return requestEnvelope<WeightVarianceReviewDto[]>(`${CASES_PATH}/${caseId}/weight-audits`, {
    method: 'GET',
  })
}

/** 建立重量差異 review（自動帶 Idempotency-Key），回傳評估結果。 */
export function createWeightVarianceReview(
  caseId: number | string,
  body: CreateWeightVarianceReviewDto,
): Promise<WeightVarianceEvalDto> {
  return requestData<WeightVarianceEvalDto>(`${CASES_PATH}/${caseId}/weight-audits`, {
    method: 'POST',
    body,
    headers: mutationHeaders(),
  })
}

/**
 * 提出 / 確認解決方案（member 或 venue 視角），回傳更新後的 review。
 * 高風險端點（spec §9.2）:需帶 2FA step-up `stepUpCode`（→ `X-2FA-Code` header）。
 */
export function submitResolution(
  reviewId: number | string,
  body: ResolutionDto,
  stepUpCode?: string,
): Promise<WeightVarianceReviewDto> {
  return requestData<WeightVarianceReviewDto>(`${REVIEWS_PATH}/${reviewId}/resolution`, {
    method: 'POST',
    body,
    headers: mutationHeaders(stepUpCode),
  })
}

export type { Envelope }
